import SearchIcon from '@mui/icons-material/Search';
import { Checkbox, FormControlLabel, InputAdornment, List, ListItem, Stack, TextField, Tooltip } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useSearch } from 'v2/hooks/useSearch/useSearch';

const LIST_MAX_HEIGHT = 300;
const searchKeys = ['name'];

export interface SearchableCheckboxListItem {
  id: string;
  name: string;
}

interface Props {
  items: SearchableCheckboxListItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  idPrefix?: string;
  disabledIds?: string[];
  disabledTooltip?: string;
}

const SearchableCheckboxList: React.FC<Props> = ({
  items,
  selectedIds,
  onChange,
  idPrefix,
  disabledIds,
  disabledTooltip
}) => {
  const { translation } = useTranslation();
  const [searchStr, setSearchStr] = React.useState('');

  const filteredItems = useSearch({ data: items, search: searchStr, keys: searchKeys });

  const handleToggle = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  return (
    <Stack spacing={1}>
      <TextField
        id={`${idPrefix ?? ''}search-input`}
        size="small"
        placeholder={translation.search}
        value={searchStr}
        onChange={(event) => setSearchStr(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }
        }}
        fullWidth
      />
      <List
        disablePadding
        sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}
      >
        {filteredItems.map((item) => {
          const isDisabled = disabledIds?.includes(item.id) ?? false;
          return (
            <ListItem
              key={item.id}
              disablePadding
              sx={(theme) => ({ backgroundColor: theme.custom.background.level2, px: 1 })}
            >
              <Tooltip title={isDisabled ? (disabledTooltip ?? '') : ''}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={`${item.id}-checkbox`}
                      checked={selectedIds.includes(item.id)}
                      onChange={() => handleToggle(item.id)}
                      size="small"
                      disabled={isDisabled}
                    />
                  }
                  label={item.name}
                  sx={{ width: '100%' }}
                />
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </Stack>
  );
};

export default SearchableCheckboxList;
